/**
 * Converted preview of what a Splitwise file spends in each currency.
 * Web and mobile both render it from here, so the numbers and the
 * "no rate, no line" rule stay identical.
 */
import { roundToCurrencyPrecision } from '@splitfiesta/shared';

import { convertAmount } from '../expense/ConversionService';
import { conversionDatesFor, isUsableRate } from './importConversion';
import type { ImportConversionRow } from './importConversion';

export interface ImportCurrencySpend {
  currency: string;
  /** How many of the given rows are in that currency. */
  rows: number;
  /** The sum of those rows' costs, at the currency's own precision. */
  amount: number;
}

export interface ImportTotalPreview {
  currency: string;
  /** The sum of the rows' costs in that currency. */
  amount: number;
  /** `null` whenever nothing can (or should) be shown. */
  converted: number | null;
}

export interface ImportRatePair {
  currency: string;
  date: string;
}

/**
 * Above this many distinct currency/date pairs the preview asks for nothing:
 * the rates endpoint answers one pair per request, and a browser queues that
 * burst behind six sockets, stalling the rest of the screen. The import
 * itself is unaffected — the server converts every row regardless.
 */
export const MAX_PREVIEW_RATE_PAIRS = 120;

/**
 * What the given rows spend per currency, in first-seen order. Callers pass
 * `importableRows`, so an assigned no-balance-effect row counts and a row that
 * will not be sent does not.
 *
 * Deliberately not `file.totals`: Splitwise's trailing `Total balance` rows
 * are a reconciliation of who owes whom, and it folds a settled currency into
 * the group one — so a file with 21 COP rows can close at `COP 0.00`, which
 * says nothing about how much will be converted on import.
 */
export function currencySpend(
  rows: ImportConversionRow[]
): ImportCurrencySpend[] {
  const spend = new Map<string, { rows: number; amount: number }>();
  for (const row of rows) {
    const entry = spend.get(row.currency) ?? { rows: 0, amount: 0 };
    entry.rows += 1;
    entry.amount += row.cost;
    spend.set(row.currency, entry);
  }
  return [...spend].map(([currency, entry]) => ({
    currency,
    rows: entry.rows,
    amount: roundToCurrencyPrecision(entry.amount, currency),
  }));
}

/**
 * Every `{ currency, date }` the preview has to look up: one per distinct pair
 * among the foreign rows given, in first-seen order. The import converts row
 * by row at the date `conversionDatesFor` gives it, so the preview needs a
 * rate per date — and only one for a settled currency.
 */
function allRatePairs(
  rows: ImportConversionRow[],
  groupCurrency: string
): ImportRatePair[] {
  const group = groupCurrency.toUpperCase();
  const dates = conversionDatesFor(rows, groupCurrency);
  const seen = new Set<string>();
  const pairs: ImportRatePair[] = [];
  for (const row of rows) {
    const currency = row.currency.toUpperCase();
    if (currency === group) continue;
    const date = dates.dateFor(row);
    const key = `${currency}|${date}`;
    if (seen.has(key)) continue;
    seen.add(key);
    pairs.push({ currency, date });
  }
  return pairs;
}

export function ratePairs(
  rows: ImportConversionRow[],
  groupCurrency: string
): ImportRatePair[] {
  const pairs = allRatePairs(rows, groupCurrency);
  return pairs.length > MAX_PREVIEW_RATE_PAIRS ? [] : pairs;
}

/**
 * True when the preview has decided not to ask for anything: a renderer that
 * would otherwise say "waiting for rates" has to say something else, because
 * nothing is on its way.
 */
export function exceedsPreviewRatePairs(
  rows: ImportConversionRow[],
  groupCurrency: string
): boolean {
  return allRatePairs(rows, groupCurrency).length > MAX_PREVIEW_RATE_PAIRS;
}

/**
 * One entry per spend currency, in the order `currencySpend` returns them, so
 * a renderer can read the two alongside each other.
 *
 * `converted` simulates the import: every row goes through `convertAmount` at
 * the rate `conversionDatesFor` picks for it — the very call the server
 * makes — and those per-row results are summed. It stays `null` for the group
 * currency and for any currency with a row whose rate has not resolved, since
 * a sum missing some of its rows would read as a smaller trip rather than as
 * a gap.
 */
export function convertedTotalsPreview(
  rows: ImportConversionRow[],
  groupCurrency: string,
  rateFor: (currency: string, date: string) => number | undefined
): ImportTotalPreview[] {
  const group = groupCurrency.toUpperCase();
  const dates = conversionDatesFor(rows, groupCurrency);
  const converted = new Map<string, number | null>();
  for (const row of rows) {
    if (row.currency.toUpperCase() === group) continue;
    const running = converted.get(row.currency);
    if (running === null) continue;
    const rate = rateFor(row.currency, dates.dateFor(row));
    if (!isUsableRate(rate)) {
      converted.set(row.currency, null);
      continue;
    }
    // The server converts the row total rounded to its own currency first
    // (Splitwise writes two decimals whatever the currency), so the preview
    // starts from the same number.
    const cost = roundToCurrencyPrecision(row.cost, row.currency);
    converted.set(
      row.currency,
      (running ?? 0) + convertAmount(cost, rate, group)
    );
  }
  return currencySpend(rows).map(entry => {
    const total = converted.get(entry.currency);
    return {
      currency: entry.currency,
      amount: entry.amount,
      converted:
        typeof total === 'number'
          ? roundToCurrencyPrecision(total, group)
          : null,
    };
  });
}
